import React, {
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { WidgetTemplatesKindEnum, WidgetTemplate } from 'graphql-common';
import { ErrorLike } from '@apollo/client';
import Search from '@lib/components/Search/Search';
import Typography from '@lib/components/Typography/Typography';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import IconButtonWithDropDown from '@lib/components/IconButtonWithDropDown/IconButtonWithDropDown';
import TabBar from '@lib/components/TabBar/TabBar';
import { TabBarItem } from '@lib/components/TabBar/constants';
import { NavItemTypeEnum } from '@lib/components/Menu/Menu';
import { materialSymbolsOutlined } from '@lib/enums/common';
import Icons from '@lib/components/Icons/Icons';
import useWidgetTemplatesList, {
  VisibilityFilter,
} from '../../hooks/useWidgetTemplatesList';
import { CREATABLE_WIDGET_TEMPLATE_KINDS } from '../../constants';
import getWidgetIconSource, {
  getWidgetMaterialIcon,
} from '../../utils/getWidgetIconSource';
import { AttachmentWithIntendedUse } from '../../utils/findAttachmentByIntendedUse';
import styles from './WidgetTemplatesSidebar.module.scss';

/** Methods exposed via ref for parent component to call */
export interface WidgetTemplatesSidebarRef {
  refetch: () => void;
  updateTemplateInList: (template: WidgetTemplate) => void;
}

interface WidgetTemplatesSidebarProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (template: WidgetTemplate) => void;
  onError?: (error: ErrorLike) => void;
  onInitialLoad?: (templates: WidgetTemplate[]) => void;
}

/**
 * Maps widget kind to Icons component for dropdown menu
 */
function getWidgetKindIcon(
  kind: WidgetTemplatesKindEnum,
): () => React.ReactNode {
  const iconMap: Record<WidgetTemplatesKindEnum, () => React.ReactNode> = {
    [WidgetTemplatesKindEnum.Announcement]: Icons.campaign,
    [WidgetTemplatesKindEnum.ExternalLink]: Icons.link,
    [WidgetTemplatesKindEnum.ActiveInterventions]: Icons.build,
    [WidgetTemplatesKindEnum.Clock]: Icons.schedule,
    [WidgetTemplatesKindEnum.QuickCreation]: Icons.add,
    [WidgetTemplatesKindEnum.SectorsPerformanceMetricsByDeadlinesMissed]:
      Icons.schedule,
    [WidgetTemplatesKindEnum.SitesPerformanceMetricsByDeadlinesMissed]:
      Icons.schedule,
    [WidgetTemplatesKindEnum.SitesMap]: Icons.locationOn,
    [WidgetTemplatesKindEnum.WeatherToday]: Icons.gridView,
    [WidgetTemplatesKindEnum.WeatherWeek]: Icons.gridView,
  };

  return iconMap[kind] || Icons.gridView;
}

/**
 * Maps widget kind to translation key
 */
function getWidgetKindLabel(kind: WidgetTemplatesKindEnum): string {
  const labelMap: Record<WidgetTemplatesKindEnum, string> = {
    [WidgetTemplatesKindEnum.ActiveInterventions]: 'active-interventions',
    [WidgetTemplatesKindEnum.Announcement]: 'announcement',
    [WidgetTemplatesKindEnum.Clock]: 'clock',
    [WidgetTemplatesKindEnum.ExternalLink]: 'external-link',
    [WidgetTemplatesKindEnum.QuickCreation]: 'quick-creation',
    [WidgetTemplatesKindEnum.SectorsPerformanceMetricsByDeadlinesMissed]:
      'sectors-performance',
    [WidgetTemplatesKindEnum.SitesPerformanceMetricsByDeadlinesMissed]:
      'sites-performance',
    [WidgetTemplatesKindEnum.SitesMap]: 'sites-map',
    [WidgetTemplatesKindEnum.WeatherToday]: 'weather-today',
    [WidgetTemplatesKindEnum.WeatherWeek]: 'weather-week',
  };
  return labelMap[kind] || kind;
}

const WidgetTemplatesSidebar = forwardRef<
  WidgetTemplatesSidebarRef,
  WidgetTemplatesSidebarProps
>(({ selectedTemplateId, onSelectTemplate, onError, onInitialLoad }, ref) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [listContainerHeight, setListContainerHeight] = useState<number | null>(
    null,
  );
  const hasCalledInitialLoad = useRef(false);
  const hasStartedLoading = useRef(false);

  // Use the templates list hook
  const {
    templates,
    counts,
    loading,
    hasMore,
    error,
    visibilityFilter,
    setVisibilityFilter,
    kindFilter,
    setKindFilter,
    loadMore,
    refetch,
    updateTemplateInList,
  } = useWidgetTemplatesList();

  // Expose refetch and updateTemplateInList to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      refetch,
      updateTemplateInList,
    }),
    [refetch, updateTemplateInList],
  );

  // Report errors to parent
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  // Notify parent when templates are initially loaded (only once)
  useEffect(() => {
    // Track if the query has started loading
    if (loading) {
      hasStartedLoading.current = true;
    }

    const hasTemplates = templates.length > 0;
    const confirmedEmpty = counts.all === 0;
    // Only consider query completed if it has started and finished loading
    const queryCompleted = hasStartedLoading.current && !loading;

    if (
      !hasCalledInitialLoad.current &&
      queryCompleted &&
      onInitialLoad &&
      (hasTemplates || confirmedEmpty)
    ) {
      hasCalledInitialLoad.current = true;
      onInitialLoad(templates);
    }
  }, [loading, templates, counts.all, onInitialLoad]);

  // Calculate and update listContainer height dynamically
  useEffect(() => {
    const updateHeight = () => {
      if (listContainerRef.current) {
        const rect = listContainerRef.current.getBoundingClientRect();
        const availableHeight = window.innerHeight - rect.top;
        setListContainerHeight(availableHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  // Tab items for visibility filter (header)
  const tabItems = useMemo<TabBarItem[]>(
    () => [
      {
        label: t('all'),
        value: 'all',
        counter: counts.all,
      },
      {
        label: t('home.public'),
        value: 'public',
        counter: counts.public,
      },
      {
        label: t('home.private'),
        value: 'private',
        counter: counts.private,
      },
    ],
    [t, counts],
  );

  const handleTabChange = useCallback(
    (item: TabBarItem) => {
      setVisibilityFilter(item.value as VisibilityFilter);
    },
    [setVisibilityFilter],
  );

  // Kind filter nav items for dropdown
  const kindFilterNavItems = useMemo(() => {
    const items = [
      {
        id: 'all-types',
        title: t('all'),
        onClick: () => setKindFilter(null),
        type: NavItemTypeEnum.PrimaryText,
        leftIcon: Icons.gridView,
        itemClassName: kindFilter === null ? 'active' : undefined,
      },
      ...CREATABLE_WIDGET_TEMPLATE_KINDS.map((kind) => ({
        id: kind,
        title: t(getWidgetKindLabel(kind)),
        onClick: () => setKindFilter(kind),
        type: NavItemTypeEnum.PrimaryText,
        leftIcon: getWidgetKindIcon(kind),
        itemClassName: kindFilter === kind ? 'active' : undefined,
      })),
    ];

    return items;
  }, [t, kindFilter, setKindFilter]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
    if (nearBottom) {
      loadMore();
    }
  }, [loading, hasMore, loadMore]);

  const renderListContent = () => {
    const isInitialLoading = loading && templates.length === 0;

    if (isInitialLoading) {
      return <CircleLoader />;
    }

    if (templates.length === 0) {
      return (
        <div className={styles.emptyList}>
          <Typography variant="body" className={styles.emptyText}>
            {t('no-items-available')}
          </Typography>
        </div>
      );
    }

    return (
      <div
        className={styles.list}
        ref={listRef}
        onScroll={handleScroll}
        style={
          listContainerHeight ? { height: listContainerHeight } : undefined
        }
      >
        {templates.map((template) => {
          const isSelected = template.id === selectedTemplateId;

          // Use shared utility for icon resolution
          const iconSource = getWidgetIconSource({
            kind: template.kind,
            attachmentsAttached:
              template.attachmentsAttached as AttachmentWithIntendedUse[],
            link: template?.fields?.link,
          });

          const renderIcon = () => {
            if (iconSource.type === 'attachment' && iconSource.url) {
              return (
                <img
                  src={iconSource.url}
                  alt=""
                  className={styles.listItemAttachment}
                />
              );
            }
            if (iconSource.type === 'favicon' && iconSource.url) {
              return (
                <img
                  src={iconSource.url}
                  alt=""
                  className={styles.listItemFavicon}
                />
              );
            }
            return (
              <MaterialIcon
                icon={iconSource.icon || 'widgets'}
                className={classNames(
                  styles.listItemMaterialIcon,
                  materialSymbolsOutlined,
                )}
              />
            );
          };

          return (
            <button
              key={template.id}
              type="button"
              className={classNames(styles.listItem, {
                [styles.listItemSelected]: isSelected,
              })}
              onClick={() => onSelectTemplate(template)}
            >
              <div className={styles.listItemIcon}>{renderIcon()}</div>
              <div className={styles.listItemContent}>
                <Typography
                  variant="body"
                  strong
                  className={styles.listItemTitle}
                >
                  {template.title}
                </Typography>
                {template.description && (
                  <Typography
                    variant="caption"
                    className={styles.listItemDescription}
                  >
                    {template.description}
                  </Typography>
                )}
              </div>
            </button>
          );
        })}
        {loading && templates.length > 0 && (
          <div className={styles.loadingMore}>
            <CircleLoader size={20} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <TabBar
        items={tabItems}
        active={visibilityFilter}
        onActiveChange={handleTabChange}
        withoutCrop
        className={styles.header}
      />
      <div className={styles.sidebar}>
        <div className={styles.searchContainer}>
          <div className={styles.search}>
            <Search
              placeholder={t('search')}
              useBottomMargin={false}
              usePagination={false}
            />
          </div>
          <div>
            <IconButtonWithDropDown
              buttonProps={{
                icon: 'filter_alt',
                counter: kindFilter ? 1 : undefined,
                symbolsOutlined: true,
              }}
              popoverProps={{
                width: 200,
                placement: 'bottomRight',
              }}
              navItems={kindFilterNavItems}
            />
          </div>
        </div>
        <div ref={listContainerRef} className={styles.listContainer}>
          {renderListContent()}
        </div>
      </div>
    </>
  );
});

WidgetTemplatesSidebar.displayName = 'WidgetTemplatesSidebar';

export default WidgetTemplatesSidebar;
